import type { PolicySummaryRow } from '@hyphengroup/cms-components/cms-blocks/PolicySummaryCard';
import type { PolicySummaryFieldKeyEnum } from '__generated__/graphql';
import type {
  PolicySummaryFieldConfig,
  PolicySummaryFieldDef,
} from './fieldConfig';
import {
  formatAddOns,
  formatBuiltUpArea,
  formatCoverType,
  formatDestination,
  formatDiscountPct,
  formatPolicyDate,
  formatPremium,
  formatResidenceType,
  formatTravellers,
  type Market,
} from './formatters';
import type {
  HomePolicySummary,
  PolicySummaryPurchase,
  TravelPolicySummary,
} from '$/types/policySummary';

export type PolicySummaryResolveContext = {
  // Active locale (en-sg / en-hk / zh-hk) — drives date + value wording.
  lang: string;
  // Market — drives currency prefix (SingSaver S$, MoneyHero HK$).
  market: Market;
  // Destination display labels resolved from insurance reference data by the
  // wrapper (WS1). Absent -> the destination formatter falls back to raw codes.
  destinationLabels?: string[];
};

type ValueResolver<T> = (
  purchase: T,
  ctx: PolicySummaryResolveContext,
  def: PolicySummaryFieldDef,
) => string | undefined;

// The code-side binding for every `PolicySummaryFieldKeyEnum` member, one
// registry per vertical (CMS-1794: TI and HI purchases are a discriminated
// union, not one shared shape). Typed as a total `Record` over the enum, so
// adding a member to the schema without a binding here is a COMPILE error
// (CMS-1794 exhaustiveness) — not a silent runtime skip.
//
// `null` means the key has no meaning for this vertical (e.g. `DESTINATION`
// for HI) — the row is always omitted. A resolver function that resolves to
// `undefined` means the key applies but this purchase has no value for it
// (optional/empty field) — also omitted.
//
// Compile-time exhaustiveness is against the enum as generated HERE, though:
// the stack deploys cms -> cms-service -> fe, so cms-service can emit a key
// this build has never seen. `buildPolicySummaryRows` therefore still looks
// the binding up defensively.
const TI_RESOLVERS: Record<
  PolicySummaryFieldKeyEnum,
  ValueResolver<TravelPolicySummary> | null
> = {
  REF_NO: (p) => p.quotationNumber || undefined,
  PREMIUM: (p, ctx) =>
    Number.isFinite(p.collectionAmount)
      ? formatPremium(p.collectionAmount, ctx.market)
      : undefined,
  DISCOUNT_PCT: (p) => formatDiscountPct(p.basePremium, p.collectionAmount),
  COVER_TYPE: (p, ctx, def) =>
    formatCoverType(p.planType, ctx.lang, def.values),
  START_DATE: (p, ctx) =>
    p.startDate ? formatPolicyDate(p.startDate, ctx.lang) : undefined,
  END_DATE: (p, ctx) =>
    p.endDate ? formatPolicyDate(p.endDate, ctx.lang) : undefined,
  ADD_ONS: (p, ctx) => formatAddOns(p, ctx.lang),
  DESTINATION: (p, ctx) =>
    formatDestination(p, ctx.destinationLabels, ctx.lang),
  TRAVELLERS: (p, ctx) => formatTravellers(p, ctx.lang),
  // HI-only (home-insurance built-up area). TI has no equivalent field.
  BUILT_UP_AREA: null,
};

const HI_RESOLVERS: Record<
  PolicySummaryFieldKeyEnum,
  ValueResolver<HomePolicySummary> | null
> = {
  REF_NO: (p) => p.quotationNumber || undefined,
  PREMIUM: (p, ctx) =>
    Number.isFinite(p.collectionAmount)
      ? formatPremium(p.collectionAmount, ctx.market)
      : undefined,
  DISCOUNT_PCT: (p) => formatDiscountPct(p.basePremium, p.collectionAmount),
  // HI has no useful COVER_TYPE source field (`planType` is a constant
  // 'HOME'). Bound instead to `residenceType` — the field that actually
  // varies, and the slot the legacy HI success screen already shows it in.
  COVER_TYPE: (p, ctx, def) =>
    formatResidenceType(p.residenceType, ctx.lang, def.values),
  START_DATE: (p, ctx) =>
    p.startDate ? formatPolicyDate(p.startDate, ctx.lang) : undefined,
  END_DATE: (p, ctx) =>
    p.endDate ? formatPolicyDate(p.endDate, ctx.lang) : undefined,
  ADD_ONS: (p, ctx) => formatAddOns(p, ctx.lang),
  // TI-only. A home policy has no destinations / traveller counts.
  DESTINATION: null,
  TRAVELLERS: null,
  BUILT_UP_AREA: (p, ctx, def) =>
    formatBuiltUpArea(p.grossArea, ctx.lang, def.values),
};

/**
 * Resolve the Policy Summary Card rows for the active vertical (CMS-1821):
 * walk the field-config in order, bind + format each field's value from the
 * purchase, and omit any row whose value is empty (optional fields, discount 0,
 * absent add-ons, …). The card never branches on vertical in its own
 * rendering — the config is the order/label source, this registry (picked by
 * `purchase.vertical`, CMS-1794) is the binding source.
 *
 * A key with no binding for the active vertical's registry is skipped rather
 * than thrown on: the registry is exhaustive over the enum this build
 * generated, but cms-service ships ahead of fe, so an unknown key is a schema-
 * skew symptom — degrade to a missing row, not a TypeError that takes down the
 * confirmation page.
 */
export const buildPolicySummaryRows = (
  config: PolicySummaryFieldConfig,
  purchase: PolicySummaryPurchase,
  ctx: PolicySummaryResolveContext,
): PolicySummaryRow[] => {
  const rows: PolicySummaryRow[] = [];

  for (const def of config.fields) {
    let value: string | undefined;
    if (purchase.vertical === 'TI') {
      const resolve = TI_RESOLVERS[def.key];
      value = resolve ? resolve(purchase, ctx, def) : undefined;
    } else {
      const resolve = HI_RESOLVERS[def.key];
      value = resolve ? resolve(purchase, ctx, def) : undefined;
    }
    if (value === undefined || value === '') {
      continue; // optional/empty field, or no binding for this vertical/build
    }
    // The mapper coalesces a missing Strapi label to '', so a label-less field
    // would otherwise render a row with a blank left column. CMS-1821 wants no
    // empty labels, so drop the row instead.
    const label = def.label.trim();
    if (!label) {
      continue;
    }
    rows.push({ id: def.key, label, value });
  }

  return rows;
};
